import React, { useState } from "react";
import { apiClient } from "../../../utils/apiClient";
import { useAlert } from "../../../hooks/useAlert";
import { Modal } from "../../../components/common/ui/modal";

import { CasePriority } from "../../../types";
import { CasePriorityBadge } from "../../../utils/caseBadges";
import { useToast } from "../../../contexts/ToastContext";

interface ChangePriorityModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  currentPriority: CasePriority;
  onSuccess?: () => void;
}

const PRIORITY_OPTIONS = [
  { value: CasePriority.LOW, label: "Low" },
  { value: CasePriority.MEDIUM, label: "Medium" },
  { value: CasePriority.HIGH, label: "High" },
  { value: CasePriority.URGENT, label: "Urgent" },
];

export const ChangePriorityModal: React.FC<ChangePriorityModalProps> = ({
  isOpen,
  onClose,
  caseId,
  currentPriority,
  onSuccess,
}) => {
  const { setAlert } = useAlert();
  const { showToast } = useToast();
  const [selectedPriority, setSelectedPriority] =
    useState<CasePriority>(currentPriority);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (selectedPriority === currentPriority) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Please select a different priority",
      });
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.updateCase(caseId, { priority: selectedPriority });

      showToast("Case priority updated successfully");

      onSuccess?.();
      onClose();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : "Failed to update priority",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Change Case Priority"
      maxWidth="500px"
      primaryAction={{
        label: submitting ? "Updating..." : "Update Priority",
        onClick: handleSubmit,
        disabled: submitting,
        loading: submitting,
      }}
      secondaryAction={{
        label: "Cancel",
        disabled: submitting,
      }}
    >
      <div className="space-y-4">
        {/* Current Priority */}
        <div className="rounded-lg">
          <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Current Priority:
          </p>
          <CasePriorityBadge priority={currentPriority} />
        </div>

        {/* Priority Selection */}
        <div>
          <label
            htmlFor="priority"
            className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            New Priority
          </label>
          <div className="space-y-2">
            {PRIORITY_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
                  selectedPriority === option.value
                    ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
                    : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                }`}
              >
                <input
                  type="radio"
                  name="priority"
                  value={option.value}
                  checked={selectedPriority === option.value}
                  onChange={(e) =>
                    setSelectedPriority(e.target.value as CasePriority)
                  }
                  className="h-4 w-4 text-brand-500 focus:ring-brand-500"
                />
                <CasePriorityBadge priority={option.value} />
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ChangePriorityModal;
